import { useState, useEffect } from 'react';
import {
  Container,
  Title,
  Text,
  Table,
  Group,
  Badge,
  ActionIcon,
  Stack,
  Alert,
  Paper,
  Button,
  Select,
  Modal,
  Tooltip,
  Code,
  ScrollArea,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconAlertCircle,
  IconRefresh,
  IconPlayerStop,
  IconClock,
  IconCheck,
  IconX,
  IconScript,
  IconApi,
  IconNetwork,
} from '@tabler/icons-react';
import {
  useTaskQueue,
  useCancelRelayTask,
  useRelayAgents,
  type QueueTask,
} from '@/hooks';
import { FogLoadingOverlay } from '@/components/fog';

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'assigned', label: 'Assigned' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'scan_script', label: 'Script Execution' },
  { value: 'ping', label: 'Ping Check' },
  { value: 'fog_api', label: 'FOG API' },
];

function getStatusColor(status: string): string {
  switch (status) {
    case 'pending':
      return 'yellow';
    case 'assigned':
      return 'blue';
    case 'completed':
      return 'green';
    case 'failed':
      return 'red';
    case 'cancelled':
      return 'gray';
    default:
      return 'gray';
  }
}

function getTypeIcon(type: string) {
  switch (type) {
    case 'scan_script':
      return <IconScript size={16} />;
    case 'ping':
      return <IconNetwork size={16} />;
    case 'fog_api':
      return <IconApi size={16} />;
    default:
      return <IconClock size={16} />;
  }
}

function getTypeName(type: string): string {
  switch (type) {
    case 'scan_script':
      return 'Script';
    case 'ping':
      return 'Ping';
    case 'fog_api':
      return 'FOG API';
    default:
      return type;
  }
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString();
}

function getPayloadPreview(payload: Record<string, unknown>): string {
  if (payload.hostname) return `Host: ${payload.hostname}`;
  if (payload.script_name) return `Script: ${payload.script_name}`;
  if (payload.device_ip) return `IP: ${payload.device_ip}`;
  return JSON.stringify(payload).slice(0, 50) + '...';
}

export function QueuePage() {
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [agentFilter, setAgentFilter] = useState<string>('');
  const [taskToCancel, setTaskToCancel] = useState<QueueTask | null>(null);
  const [cancelModalOpen, { open: openCancelModal, close: closeCancelModal }] = useDisclosure(false);
  const [pageVisible, setPageVisible] = useState(false);
  const [visibleRows, setVisibleRows] = useState<Set<number>>(new Set());

  const { data, isLoading, error, refetch, isRefetching } = useTaskQueue({
    status: statusFilter || undefined,
    type: typeFilter || undefined,
    agent_id: agentFilter || undefined,
    limit: 100,
  });

  const { data: agents } = useRelayAgents();
  const cancelMutation = useCancelRelayTask();

  useEffect(() => {
    setPageVisible(true);
  }, []);

  // Animate rows
  useEffect(() => {
    if (data?.tasks) {
      setVisibleRows(new Set());
      data.tasks.forEach((_, index) => {
        setTimeout(() => {
          setVisibleRows((prev) => new Set([...prev, index]));
        }, index * 30);
      });
    }
  }, [data?.tasks]);

  const agentOptions = [
    { value: '', label: 'All Agents' },
    ...(agents?.map((a) => ({ value: a.id, label: a.name })) || []),
  ];

  const handleCancelClick = (task: QueueTask) => {
    setTaskToCancel(task);
    openCancelModal();
  };

  const handleConfirmCancel = async () => {
    if (!taskToCancel) return;

    try {
      await cancelMutation.mutateAsync(taskToCancel.id);
      closeCancelModal();
      setTaskToCancel(null);
    } catch (error) {
      // Error handled by mutation
    }
  };

  if (isLoading) {
    return (
      <Container size="xl" py="md">
        <FogLoadingOverlay message="Loading Task Queue..." />
      </Container>
    );
  }

  if (error) {
    return (
      <Container size="xl" py="md">
        <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
          Failed to load task queue: {error.message}
        </Alert>
      </Container>
    );
  }

  const summary = data?.summary || { pending: 0, assigned: 0, completed: 0, failed: 0, cancelled: 0 };
  const tasks = data?.tasks || [];
  const activeTasks = tasks.filter((t) => t.status === 'pending' || t.status === 'assigned');

  return (
    <Container
      size="xl"
      py="md"
      style={{
        opacity: pageVisible ? 1 : 0,
        transform: pageVisible ? 'translateY(0)' : 'translateY(10px)',
        transition: 'all 300ms cubic-bezier(0.16, 1, 0.3, 1)',
      }}
    >
      {/* Header */}
      <Group justify="space-between" mb="lg">
        <div>
          <Title order={2}>Task Queue</Title>
          <Text c="dimmed" size="sm">
            {activeTasks.length} active task{activeTasks.length !== 1 ? 's' : ''}, {data?.total || 0} total
          </Text>
        </div>
        <Group>
          <Button
            variant="light"
            leftSection={<IconRefresh size={16} />}
            onClick={() => refetch()}
            loading={isRefetching}
          >
            Refresh
          </Button>
        </Group>
      </Group>

      {/* Summary Cards */}
      <Group mb="md" gap="sm">
        <Paper withBorder p="sm" radius="md" style={{ flex: 1 }}>
          <Group gap="xs">
            <IconClock size={18} color="var(--mantine-color-yellow-6)" />
            <div>
              <Text size="xs" c="dimmed">Pending</Text>
              <Text fw={600}>{summary.pending}</Text>
            </div>
          </Group>
        </Paper>
        <Paper withBorder p="sm" radius="md" style={{ flex: 1 }}>
          <Group gap="xs">
            <IconClock size={18} color="var(--mantine-color-blue-6)" />
            <div>
              <Text size="xs" c="dimmed">Assigned</Text>
              <Text fw={600}>{summary.assigned}</Text>
            </div>
          </Group>
        </Paper>
        <Paper withBorder p="sm" radius="md" style={{ flex: 1 }}>
          <Group gap="xs">
            <IconCheck size={18} color="var(--mantine-color-green-6)" />
            <div>
              <Text size="xs" c="dimmed">Completed</Text>
              <Text fw={600}>{summary.completed}</Text>
            </div>
          </Group>
        </Paper>
        <Paper withBorder p="sm" radius="md" style={{ flex: 1 }}>
          <Group gap="xs">
            <IconX size={18} color="var(--mantine-color-red-6)" />
            <div>
              <Text size="xs" c="dimmed">Failed</Text>
              <Text fw={600}>{summary.failed}</Text>
            </div>
          </Group>
        </Paper>
      </Group>

      {/* Filters */}
      <Paper withBorder p="md" mb="md" radius="md">
        <Group>
          <Select
            placeholder="Filter by status"
            data={STATUS_OPTIONS}
            value={statusFilter}
            onChange={(v) => setStatusFilter(v || '')}
            clearable
            style={{ width: 180 }}
          />
          <Select
            placeholder="Filter by type"
            data={TYPE_OPTIONS}
            value={typeFilter}
            onChange={(v) => setTypeFilter(v || '')}
            clearable
            style={{ width: 180 }}
          />
          <Select
            placeholder="Filter by agent"
            data={agentOptions}
            value={agentFilter}
            onChange={(v) => setAgentFilter(v || '')}
            clearable
            style={{ width: 200 }}
          />
          {(statusFilter || typeFilter || agentFilter) && (
            <Button
              variant="subtle"
              size="sm"
              onClick={() => {
                setStatusFilter('');
                setTypeFilter('');
                setAgentFilter('');
              }}
            >
              Clear Filters
            </Button>
          )}
        </Group>
      </Paper>

      {/* Auto-refresh indicator */}
      {activeTasks.length > 0 && (
        <Alert color="blue" mb="md" icon={<IconClock size={16} />}>
          <Group justify="space-between">
            <Text size="sm">
              {activeTasks.length} task{activeTasks.length !== 1 ? 's' : ''} in queue
            </Text>
            <Text size="xs" c="dimmed">
              Auto-refreshing every 5 seconds
            </Text>
          </Group>
        </Alert>
      )}

      {/* Tasks Table */}
      <Paper withBorder radius="md">
        <ScrollArea>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Type</Table.Th>
                <Table.Th>Payload</Table.Th>
                <Table.Th>Agent</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Created</Table.Th>
                <Table.Th w={80}>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {tasks.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={6}>
                    <Text ta="center" c="dimmed" py="xl">
                      No tasks found
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                tasks.map((task, index) => (
                  <Table.Tr
                    key={task.id}
                    style={{
                      opacity: visibleRows.has(index) ? 1 : 0,
                      transform: visibleRows.has(index) ? 'translateX(0)' : 'translateX(-10px)',
                      transition: 'all 300ms cubic-bezier(0.16, 1, 0.3, 1)',
                    }}
                  >
                    <Table.Td>
                      <Group gap="xs">
                        {getTypeIcon(task.type)}
                        <Badge variant="light" color={task.type === 'scan_script' ? 'violet' : 'blue'}>
                          {getTypeName(task.type)}
                        </Badge>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Tooltip label={<Code block>{JSON.stringify(task.payload, null, 2)}</Code>} multiline w={400}>
                        <Text size="sm" lineClamp={1}>
                          {getPayloadPreview(task.payload)}
                        </Text>
                      </Tooltip>
                      {task.error && (
                        <Text size="xs" c="red" lineClamp={1}>
                          {task.error}
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      {task.agent_name ? (
                        <Badge variant="outline" size="sm">
                          {task.agent_name}
                        </Badge>
                      ) : (
                        <Text size="sm" c="dimmed">Unassigned</Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Badge color={getStatusColor(task.status)}>
                        {task.status}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={2}>
                        <Text size="xs">{formatDate(task.created_at)}</Text>
                        {task.completed_at && (
                          <Text size="xs" c="dimmed">
                            Done: {formatDate(task.completed_at)}
                          </Text>
                        )}
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      {task.can_cancel && (
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          onClick={() => handleCancelClick(task)}
                          title="Cancel task"
                        >
                          <IconPlayerStop size={16} />
                        </ActionIcon>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      </Paper>

      {/* Cancel Confirmation Modal */}
      <Modal
        opened={cancelModalOpen}
        onClose={closeCancelModal}
        title="Cancel Task"
        size="sm"
      >
        <Stack gap="md">
          <Text>
            Are you sure you want to cancel this task?
          </Text>
          {taskToCancel && (
            <Paper withBorder p="sm" radius="md" bg="gray.0">
              <Stack gap={4}>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">Type</Text>
                  <Badge size="sm" color={taskToCancel.type === 'scan_script' ? 'violet' : 'blue'}>
                    {getTypeName(taskToCancel.type)}
                  </Badge>
                </Group>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">Payload</Text>
                  <Text size="sm">{getPayloadPreview(taskToCancel.payload)}</Text>
                </Group>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">Status</Text>
                  <Badge size="sm" color={getStatusColor(taskToCancel.status)}>
                    {taskToCancel.status}
                  </Badge>
                </Group>
              </Stack>
            </Paper>
          )}
          <Group justify="flex-end">
            <Button variant="subtle" onClick={closeCancelModal}>
              Keep Running
            </Button>
            <Button
              color="red"
              onClick={handleConfirmCancel}
              loading={cancelMutation.isPending}
            >
              Cancel Task
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}

export default QueuePage;
